import { Middleware } from "redux";
import { CharacterInitializer } from "../../entities/characters/Character";
import { AdventureActions } from "../../store/stores/adventures/AdventureStore.Actions";
import { CharacterActions } from "../../store/stores/collection/characters/CharacterStore.Actions";
import { UserActions } from "../../store/stores/players/PlayerStore.Actions";
import { AppStore, BaseHub } from "../Hub.Base";
import { AccountApiConfig } from "./config/AccountApiConfig";
import { AdventureHubConfig } from "./config/AdventureHubConfig";
import { ApplicationState } from "../../store/stores/ApplicationState";
import { AdventureHubAction } from "../../store/stores/api/ApiStore.Actions";
import { CommunalDice } from "../../entities/adventures/Adventure";
import { ApiResponse } from "../responses/ApiResponse";
import { HubResponse } from "../responses/HubResponse";
import { HandTypeValue, RollRequest, RollResponse } from "../../entities/rolls/Roll";

export class AdventureHub extends BaseHub<typeof AdventureHubConfig> {
    protected get audience(): string {
        return AccountApiConfig.audience;
    }
    
    public async start(): Promise<void> {
        await super.start();
        const connection = await this.connection;

        this.invokeDispatch(UserActions.setAdventureConnection)(connection.connectionId);
        connection.onclose(() => this.invokeDispatch(UserActions.setAdventureConnection)(null));

        connection.on('PlayerJoined', this.invokeDispatch(AdventureActions.playerJoined));
        connection.on('PlayerLeft', this.invokeDispatch(AdventureActions.playerLeft));
        connection.on('PushNewEvent', this.invokeDispatch(AdventureActions.pushNewEvent));
        connection.on('CharacterAdded', (id: string, character: CharacterInitializer) => {
            this.invokeDispatch(CharacterActions.receiveUnownedCharacter)(character);
            this.invokeDispatch(AdventureActions.receiveCharacterAdded)(id, character.id);
        });
        connection.on('CharacterRemoved', this.invokeDispatch(AdventureActions.receivedCharacterRemoved));
        connection.on('SetCommunalDice', this.invokeDispatch(AdventureActions.receiveCommunalDice));
        connection.on('PlayerRolledDice', this.invokeDispatch(CharacterActions.rollCharacterDice));
    }

    private static instance: AdventureHub | null = null;

    public static CreateInstance(store: AppStore, config: typeof AdventureHubConfig) {
        return AdventureHub.instance = new AdventureHub(store, config);
    }

    public static Instance(): AdventureHub {
        if (this.instance == null) {
            throw new Error("Attempted to retrieve hub instance before instantiation.");
        }
        return this.instance;
    }

    public static CreateMiddleware(): Middleware[] {
        return [
            ({ getState }) => (next) => (action: unknown) => {
                try {
                    AdventureHub.Reduce(getState, action)
                }
                catch (e) {
                    console.error(e);
                }
                finally {
                    next(action ?? { type: "" });
                }
            }
        ]
    }

    public static Reduce(getState: () => ApplicationState, incomingAction: unknown) {
        const action = incomingAction as AdventureHubAction;
        switch (action.type) {
            default: BaseHub.Reduce(getState, action);
        }
    }

    public get name(): string {
        return "AdventureHub";
    }

    /**
     * Rolls the adventure's communal dice.
     * @param id The adventure's unique identifier.
     * @returns The rolled dice values.
     */
    public async rollCommunalDice(id: string): Promise<ApiResponse<CommunalDice>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<CommunalDice> = await connection.invoke("rollCommunalDice", id);
        return this.createResponse(hubResponse);
    }


    /**
     * Sets an adventure's communal dice values.
     * @param connectionId The adventure connection id used by this instance.
     * @param id The adventure's unique identifier.
     * @param communalDice The communal dice values to set.
     */
    public async setCommunalDice(id: string, communalDice: CommunalDice): Promise<ApiResponse<void>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<void> = await connection.invoke("setCommunalDice", id, communalDice);
        return this.createResponse(hubResponse);
    }

    /**
     * Joins an adventure.
     * @param inviteId The adventure's unique invite id.
     * @returns The adventure's unique identifierr.
     */
    public async joinAdventure(inviteId: string): Promise<ApiResponse<string>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<string> = await connection.invoke("joinAdventure", inviteId);
        return this.createResponse(hubResponse);
    }

    /**
     * Leaves an adventure.
     * @param id The adventure's unique identifier.
     */
    public async leaveAdventure(id: string): Promise<ApiResponse<void>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<void> = await connection.invoke("leaveAdventure", id);
        return this.createResponse(hubResponse);
    }

    /**
     * Adds a character to the requested adventure.
     * @param adventureId The adventure's unique identifier.
     * @param characterId The character's unique identifier.
     */
    public async addCharacterToAdventure(adventureId: string, characterId: string): Promise<ApiResponse<void>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<void> = await connection.invoke("addCharacterToAdventure", adventureId, characterId);
        return this.createResponse(hubResponse);
    }

    /**
     * Removes a character from the requested adventure.
     * @param adventureId The adventure's unique identifier.
     * @param characterId The character's unique identifier.
     */
    public async removeCharacterFromAdventure(adventureId: string, characterId: string): Promise<ApiResponse<void>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<void> = await connection.invoke("removeCharacterFromAdventure", adventureId, characterId);
        return this.createResponse(hubResponse);
    }

    /**
     * Rolls a character's dice.
     * @param characterId The character to roll for.
     * @param rollRequest Information about the requested roll.
     * @returns The result of the roll operation.
     */
    public async rollForPlayer(characterId: string, rollRequest: RollRequest): Promise<ApiResponse<RollResponse>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<RollResponse> = await connection.invoke("rollForPlayer", characterId, rollRequest);
        return this.createResponse(hubResponse);
    }

    /**
     * Declare a hand for a given character.
     * @param characterId The unique identifier of the character to declare the hand for.
     * @param handType The hand to declare.
     */
    public async declareHand(characterId: string, handType: HandTypeValue): Promise<ApiResponse<void>> {
        const connection = await this.connection;
        const hubResponse: HubResponse<void> = await connection.invoke("declareHand", characterId, handType);
        return this.createResponse(hubResponse);
    }
}